<!--
 * @Description: 顶部入口卡片组件
-->
<template>
    <!-- 大卡片样式：image / video -->
    <NuxtLink v-if="isLargeCard" class="banner-card block relative overflow-hidden cursor-pointer rounded-[12px]"
        :to="item.path">
        <!-- 背景图片：贴底裁切，避免宽屏下插画被居中裁掉、上方留白过大 -->
        <img :src="backgroundImage" alt="background"
            class="absolute inset-0 w-full h-full object-cover object-bottom" loading="eager" decoding="async"
            :fetchpriority="priority ? 'high' : 'auto'" crossorigin="anonymous" :width="imgWidth"
            :height="imgHeight" />

        <!-- 内容层 -->
        <div class="relative z-10 p-4 flex flex-col h-full justify-start">
            <!-- 标题 -->
            <h3 class="text-white text-[16px] font-medium leading-tight">
                {{ item.title }}
            </h3>

            <!-- 简介 -->
            <div class="flex items-center mt-1">
                <span class="text-white/70 text-[12px] leading-tight">
                    {{ description }}
                </span>
            </div>
        </div>

        <!-- go-click 绝对定位在右上角 -->
        <img :src="goClickIcon" alt="go" class="absolute z-10 w-[48px] h-[32px]" style="top: 16px; right: 16px" />
    </NuxtLink>

    <!-- 小卡片样式：music / character -->
    <NuxtLink v-else class="small-card flex items-center justify-between px-4 cursor-pointer rounded-[12px]"
        :to="item.path">
        <!-- 左侧：文字内容 -->
        <div class="flex flex-col justify-center">
            <!-- 标题 + 箭头 -->
            <div class="flex items-center">
                <span class="text-white text-[16px] font-medium">{{ item.title }}</span>
                <img :src="goClickIcon" alt="go" class="w-[24px] h-[16px] ml-1 flex-shrink-0" />
            </div>
            <!-- 简介 -->
            <span class="text-white/70 text-[12px] mt-1">{{ description }}</span>
        </div>

        <!-- 右侧：图标 -->
        <img :src="smallCardIcon" alt="icon" class="w-[48px] h-[48px] flex-shrink-0" />
    </NuxtLink>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from '~/composables/useI18n'

const { t } = useI18n()

// 引入本地背景图片
import imageBg from '~/assets/images/common/image-bg.png'
import videoBg from '~/assets/images/common/video-bg.png'
// 引入点击图标
import goClickIcon from '~/assets/images/common/go-click.png'
// 引入小卡片图标
import musicIcon from '~/assets/images/common/music-icon.png'
import characterIcon from '~/assets/images/common/character-icon.png'

interface BannerItem {
    title: string
    path: string
}

const props = defineProps<{
    item: BannerItem
    // 卡片类型：image（图片生成）、video（视频生成）、music（音乐生成）、character（角色）
    type?: 'image' | 'video' | 'music' | 'character'
    /**
     * 是否作为首屏关键资源（LCP 候选）
     * - true：给 <img> 加 fetchpriority="high"，让浏览器优先拉取
     * - false：正常优先级
     */
    priority?: boolean
}>()

// 判断是否是大卡片（image/video 文字在顶部）
const isLargeCard = computed(() => {
    return props.type === 'image' || props.type === 'video'
})

// 根据 type 获取对应背景图（大卡片使用）
const backgroundImage = computed(() => {
    switch (props.type) {
        case 'image':
            return imageBg
        case 'video':
            return videoBg
        default:
            return imageBg
    }
})

// 根据 type 获取小卡片图标
const smallCardIcon = computed(() => {
    switch (props.type) {
        case 'music':
            return musicIcon
        case 'character':
            return characterIcon
        default:
            return musicIcon
    }
})

// 根据 type 获取简介文案
const description = computed(() => {
    switch (props.type) {
        case 'image':
            return t('homeView.bannerImageDesc')
        case 'video':
            return t('homeView.bannerVideoCreateDesc')
        case 'music':
            return t('homeView.bannerMusicDesc') || 'Create AI Music'
        case 'character':
            // return t('homeView.bannerCharacterDesc')
            return t('homeView.bannerVideoEffectsDesc')
        default:
            return ''
    }
})

/**
 * LCP/CLS 优化：
 * - 给图片补齐 width/height，帮助浏览器更早确定布局与解码策略
 */
const imgWidth = computed(() => (isLargeCard.value ? 320 : 268))
const imgHeight = computed(() => (isLargeCard.value ? 220 : 104))
</script>

<style lang="less" scoped>
.banner-card {
    display: block;
    height: 100%;

    &:hover {
        opacity: 0.9;
    }
}

.small-card {
    display: flex;
    height: 100%;
    background-color: rgba(28, 28, 30, 1);
    border: 1px solid rgba(44, 44, 44, 1);

    &:hover {
        opacity: 0.9;
    }
}
</style>
